import { useQuery } from "@apollo/client/react";
import {
  copyNotesToPatientRedirect,
  moveNotesToPatientRedirect,
} from "@components/common/multiselect/utils";
import { DSCircularButton } from "@components/design-system/molecules";
import { faClone } from "@fortawesome/free-regular-svg-icons";
import {
  faInfoCircle,
  faTrash,
  faTruckMoving,
  faTimes,
  faPeopleArrows,
  faUpRightFromSquare,
  faLinkSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import getPatientsQuery from "@graphql/queries/GetPatients";
import {
  useNoteMultiselectContext,
  useNoteMultiselect,
} from "@hooks";
import { useTeam } from "@hooks";
import { useCompanion } from "@hooks/use-companion";
import { Tooltip } from "@mui/material";
import { config, animated, useTransition } from "@react-spring/web";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { MultiSelectActionButton } from "./MultiSelectActionButton";
import { TransferMultiModal } from "./TransferMultiModal";
import { UnassignFromPatientModal } from "./UnassignFromPatientModal";

export function MultiSelectActionContainer({ notes, refetch }) {
  const navigate = useNavigate();
  const { currentTeam } = useTeam();
  const { sidebarWidth } = useCompanion();

  const {
    numSelectedNotes,
    selectedNoteUuids,
    setShouldShowDeleteNoteModal,
    shouldShowDeleteNoteModal,
    resetSelectedNotesList,
  } = useNoteMultiselectContext();

  const { shouldAllowCopyAction } = useNoteMultiselect({
    notes,
    refetch,
  });

  const [isModalShowing, setIsModalShowing] = useState(false);
  const [isUnassignModalOpen, setIsUnassignModalOpen] =
    useState(false);
  const { transferNotes: isTransferNotesEnabled } = useFlags();

  const { data, loading } = useQuery(getPatientsQuery, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    resetSelectedNotesList();
  }, [currentTeam?.uuid, resetSelectedNotesList]);

  const selectedNotesWithPatient =
    notes?.filter(
      (note) => selectedNoteUuids[note?.uuid] && note?.patient?.uuid,
    ) ?? [];
  const hasAssignedNotes = selectedNotesWithPatient.length > 0;

  const transitions = useTransition(numSelectedNotes > 0, {
    from: { opacity: 0, y: 120 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 120 },
    config: config.default,
  });

  if (shouldShowDeleteNoteModal || loading) {
    return null;
  }

  return transitions((styles, item) =>
    item ? (
      <animated.div
        style={{ ...styles, paddingLeft: sidebarWidth || 0 }}
        className="fixed bottom-0 left-0 z-30 w-full flex flex-col items-center justify-center pointer-events-none"
      >
        <TransferMultiModal
          notes={notes}
          shouldShowTransferModal={isModalShowing}
          setShouldShowTransferModal={setIsModalShowing}
          refetch={refetch}
        />

        <UnassignFromPatientModal
          isOpen={isUnassignModalOpen}
          onClose={() => setIsUnassignModalOpen(false)}
          notes={selectedNotesWithPatient}
          refetch={refetch}
          onSuccess={resetSelectedNotesList}
        />

        <div
          style={{
            boxShadow: "0px -5px 50px -10px rgba(0,0,0,0.40)",
          }}
          className="relative flex flex-col items-center bg-primary pt-4 pb-2 px-4 md:px-6 rounded-t-2xl pointer-events-auto max-w-full"
        >
          <div className="absolute top-4 right-4">
            <DSCircularButton
              icon={faTimes}
              variant="danger"
              size="sm"
              onClick={resetSelectedNotesList}
              dataTestId="multiselect-close-button"
            />
          </div>
          <div className="flex flex-row items-center justify-between w-full">
            <h4 className="flex-1 text-center mb-3 text-secondary">
              ⚡️ {numSelectedNotes} note
              {numSelectedNotes > 1 ? "s" : ""} selected
            </h4>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {numSelectedNotes > 1 && (
              <Tooltip
                arrow
                title="Please ensure you have popups enabled or this feature will not work as expected."
              >
                <div>
                  <MultiSelectActionButton
                    onClickActionDescription="Open all in new tabs"
                    onClick={() => {
                      Object.keys(selectedNoteUuids).forEach(
                        (noteUuid) =>
                          window.open(
                            `/edit_note/${noteUuid}`,
                            "_blank",
                          ),
                      );
                    }}
                    icon={faUpRightFromSquare}
                  />
                </div>
              </Tooltip>
            )}
            {data?.patients?.length > 0 && (
              <MultiSelectActionButton
                onClickActionDescription="Move to patient"
                onClick={() =>
                  moveNotesToPatientRedirect({
                    selectedNoteUuids,
                    navigate,
                  })
                }
                icon={faTruckMoving}
                disabled={!shouldAllowCopyAction}
              />
            )}
            {hasAssignedNotes && (
              <MultiSelectActionButton
                onClickActionDescription="Unassign from patient"
                onClick={() => setIsUnassignModalOpen(true)}
                icon={faLinkSlash}
                disabled={!shouldAllowCopyAction}
              />
            )}
            <MultiSelectActionButton
              onClickActionDescription="Duplicate to patient"
              onClick={() =>
                copyNotesToPatientRedirect({
                  selectedNoteUuids,
                  navigate,
                })
              }
              icon={faClone}
              disabled={!shouldAllowCopyAction}
            />
            {isTransferNotesEnabled && currentTeam?.uuid && (
              <MultiSelectActionButton
                onClickActionDescription="Transfer ownership"
                onClick={() => setIsModalShowing(true)}
                icon={faPeopleArrows}
                disabled={!shouldAllowCopyAction || hasAssignedNotes}
              />
            )}
            <MultiSelectActionButton
              onClickActionDescription="Delete"
              onClick={() => setShouldShowDeleteNoteModal(true)}
              icon={faTrash}
              variant="danger"
            />
          </div>
          {!shouldAllowCopyAction && (
            <h5 className="text-xs text-placeholder pt-2 text-center">
              <FontAwesomeIcon icon={faInfoCircle} /> You can't move,
              duplicate, or transfer notes labeled
              &apos;Processing&apos; or &apos;New Recording&apos;.
            </h5>
          )}
          {hasAssignedNotes && (
            <h5 className="text-xs text-placeholder pt-2 text-center">
              <FontAwesomeIcon icon={faInfoCircle} /> You can't
              transfer notes that are assigned to a patient.
            </h5>
          )}
        </div>
      </animated.div>
    ) : null,
  );
}
